import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Content() {
  useEffect(() => {
    document.title =
      "Full-Service Ecommerce Content Agency | Photo, Video, Web, Music, Ads | Evolve Media";

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
      if (!el) {
        if (selector.startsWith("link")) {
          el = document.createElement("link");
          (el as HTMLLinkElement).setAttribute("rel", "canonical");
        } else {
          el = document.createElement("meta");
          const name = selector.match(/name='([^']+)'/)?.[1] || selector.match(/property='([^']+)'/)?.[1];
          if (name) {
            if (selector.includes("property=")) (el as HTMLMetaElement).setAttribute("property", name);
            else (el as HTMLMetaElement).setAttribute("name", name);
          }
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta("link[rel='canonical']", "href", "https://evolvemediaagency.com/content");
    setMeta(
      "meta[name='description']",
      "content",
      "One agency for all of your ecommerce content: product photo, video, music, Shopify, WordPress, Amazon brand stores, custom websites, graphic design, copy, email, landing pages, and paid ads on Facebook, TikTok, and Google."
    );
    setMeta("meta[property='og:title']", "content", "Full-Service Ecommerce Content Agency | Evolve Media");
    setMeta(
      "meta[property='og:description']",
      "content",
      "Photo, video, music, web, design, copy, email, and paid ads — every piece of content your brand needs, produced by one team."
    );
    setMeta("meta[property='og:url']", "content", "https://evolvemediaagency.com/content");
    setMeta("meta[property='og:type']", "content", "website");

    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Full-Service Ecommerce Content Production",
      provider: {
        "@type": "Organization",
        name: "Evolve Media Agency",
        url: "https://evolvemediaagency.com",
      },
      areaServed: "Worldwide",
      serviceType: [
        "Product Photography",
        "Product Video Production",
        "Custom Music & Sonic Branding",
        "Shopify Web Development",
        "WordPress Web Development",
        "Amazon Brand Store Design",
        "Custom Website Development",
        "Graphic Design",
        "Copywriting",
        "Email Marketing",
        "Landing Pages & Sales Funnels",
        "Paid Advertising (Facebook, TikTok, Google)",
        "Creative Split Testing",
      ],
      url: "https://evolvemediaagency.com/content",
    });
    document.head.appendChild(jsonLd);

    return () => {
      if (jsonLd.parentNode) jsonLd.parentNode.removeChild(jsonLd);
    };
  }, []);

  const sectionCard =
    "bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-gray-100 hover:shadow-xl transition-shadow";

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <Navbar />

      {/* HERO */}
      <section className="bg-black text-white py-20 md:py-28 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-cta-green font-extrabold tracking-widest text-xs md:text-sm uppercase mb-5">
            One Agency. Every Piece of Content.
          </p>
          <h1 className="font-black text-4xl md:text-6xl leading-tight mb-6">
            Every Piece of Content<br />
            Your Brand Needs.<br />
            <span className="text-cta-green">One Team.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            Photo. Video. Music. Shopify. WordPress. Amazon brand stores. Custom websites. Graphic
            design. Copy. Email. Landing pages. Sales funnels. Paid ads on Facebook, TikTok, and
            Google. Evolve Media is the in-house creative team your brand has been trying to hire —
            without the overhead.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/evolvemedia/amz-30-min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-cta-green text-white font-bold px-8 py-4 rounded-full hover:opacity-90 transition text-base"
            >
              Book a Free Strategy Call
            </a>
            <a
              href="https://emapdf.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-cta-blue text-white font-bold px-8 py-4 rounded-full hover:opacity-90 transition text-base"
            >
              Get the Free Ecom Profit Box
            </a>
          </div>
          <p className="text-sm text-gray-500 mt-8">
            Trusted by 7-figure Amazon, Shopify, and DTC brands across the U.S., Canada, and the U.K.
          </p>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 md:py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <p className="text-cta-blue font-extrabold tracking-widest text-xs uppercase mb-4">
            The Problem
          </p>
          <h2 className="font-black text-3xl md:text-4xl text-gray-900 mb-6 leading-tight">
            Most ecommerce brands are bleeding money to six different vendors — and still don't have
            content that actually sells.
          </h2>
          <div className="space-y-5 text-gray-700 text-lg leading-relaxed">
            <p>
              You hired a photographer for product shots. A freelancer for your Shopify site. Someone
              else for your Amazon listings. A copywriter on Upwork. A different person for graphic
              design. Maybe a music guy. Maybe an ads agency. Maybe a video team for that one TikTok
              project.
            </p>
            <p>
              And now your brand looks like seven different brands stitched together. Your photo style
              doesn't match your video style. Your Shopify voice doesn't match your Amazon copy. Your
              ads use stock music while your brand store has none. Each vendor blames the other when
              conversion drops.
            </p>
            <p className="font-bold text-gray-900">
              That is the content gap that's costing you money — the difference between the assets you
              have and the assets that would actually convert.
            </p>
            <p>
              Evolve Media closes that gap. Every service below is produced by the same team, briefed
              from the same brand strategy, optimized against the same conversion benchmarks. One team.
              One creative direction. One set of numbers we hold ourselves to.
            </p>
          </div>
        </div>
      </section>

      {/* VISUAL CONTENT PRODUCTION */}
      <section className="py-20 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-cta-green font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Visual Content Production
          </p>
          <h2 className="font-black text-3xl md:text-5xl text-gray-900 mb-4 text-center leading-tight">
            Photo, Video, and Sound — Built to Convert.
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-14 leading-relaxed">
            Every visual we produce is engineered backwards from a single question: will this asset
            increase click-through rate, conversion rate, or average order value? If the answer isn't
            yes, we rebuild it.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <div className={sectionCard}>
              <div className="text-4xl mb-4">📸</div>
              <h3 className="font-extrabold text-2xl text-gray-900 mb-3">Product Photography</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Pure white background hero images that pass Amazon's strictest guidelines. Lifestyle
                photography that shows your product in real customer context. Infographic stacks that
                turn benefits into glanceable visuals. Macro shots, scale shots, packaging shots,
                comparison shots — everything you need to fill a 7-image gallery and keep customers
                scrolling.
              </p>
              <ul className="text-gray-600 text-sm space-y-2 mb-5">
                <li>✓ Amazon TOS-compliant white background hero</li>
                <li>✓ Lifestyle scenes with real models</li>
                <li>✓ Annotated benefit infographics</li>
                <li>✓ A+ Content & brand store imagery</li>
                <li>✓ Shopify PDP gallery sets</li>
              </ul>
              <Link
                to="/product-photos"
                className="text-cta-green font-bold hover:underline text-sm"
              >
                See the photo portfolio →
              </Link>
            </div>

            <div className={sectionCard}>
              <div className="text-4xl mb-4">🎥</div>
              <h3 className="font-extrabold text-2xl text-gray-900 mb-3">Product Video</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sponsored Brand video for Amazon. Product demonstration video for your PDP and Shopify.
                UGC-style creator content. Native-feeling TikTok and Reels videos. Long-form YouTube
                product reviews. Every format, every aspect ratio, scripted and edited around the
                conversion moment your product needs to win.
              </p>
              <ul className="text-gray-600 text-sm space-y-2 mb-5">
                <li>✓ Amazon Sponsored Brand video (15s/30s)</li>
                <li>✓ Product demo and how-it-works video</li>
                <li>✓ UGC and creator-led ad video</li>
                <li>✓ TikTok / Reels / YouTube Shorts</li>
                <li>✓ Long-form review and unboxing</li>
              </ul>
              <Link
                to="/amazon-product-video-production"
                className="text-cta-green font-bold hover:underline text-sm"
              >
                Explore video production →
              </Link>
            </div>

            <div className={sectionCard}>
              <div className="text-4xl mb-4">🎵</div>
              <h3 className="font-extrabold text-2xl text-gray-900 mb-3">Music & Sonic Branding</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Custom-composed jingles and brand themes — no stock loops, no Epidemic Sound clichés.
                Your brand gets a recognizable sonic identity that runs through every Sponsored Brand
                video, every TikTok ad, every Shopify product video, and every podcast read. We've
                produced 63 original tracks for ecommerce brands and counting.
              </p>
              <ul className="text-gray-600 text-sm space-y-2 mb-5">
                <li>✓ 100% original, fully licensed compositions</li>
                <li>✓ 15s and 30s ad-ready cuts</li>
                <li>✓ Logo stings and audio idents</li>
                <li>✓ Long-form brand themes</li>
                <li>✓ Vocal toplines and brand jingles</li>
              </ul>
              <Link
                to="/custom-jingles-sonic-branding"
                className="text-cta-green font-bold hover:underline text-sm"
              >
                Listen to all 63 tracks →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WEB & DIGITAL STOREFRONTS */}
      <section className="py-20 md:py-24 px-4 bg-black text-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-cta-green font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Web & Digital Storefronts
          </p>
          <h2 className="font-black text-3xl md:text-5xl mb-4 text-center leading-tight">
            Stores, Sites, and Storefronts That Convert.
          </h2>
          <p className="text-lg text-gray-400 text-center max-w-3xl mx-auto mb-14 leading-relaxed">
            We don't build "websites" — we build conversion engines. Every layout decision, every
            content block, every image placement is backed by ecommerce CRO best practices and
            tested against real customer behavior.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <div className="text-3xl mb-3">🛍️</div>
              <h3 className="font-extrabold text-2xl mb-3">Shopify Site Builds</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Custom Shopify themes built on Dawn or Online Store 2.0, structured around the exact
                conversion architecture top DTC brands use. Optimized PDPs, sticky add-to-carts, post-
                purchase upsells, mobile-first design, sub-2-second load times. Built to scale from
                $10K to $10M months without rebuilding.
              </p>
              <Link
                to="/shopify-web-development"
                className="text-cta-green font-bold hover:underline text-sm"
              >
                See the Shopify service →
              </Link>
            </div>

            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <div className="text-3xl mb-3">📰</div>
              <h3 className="font-extrabold text-2xl mb-3">WordPress Sites</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                For brands that need a content hub, blog network, course platform, or service-business
                site, we build on WordPress with a custom or premium theme foundation. SEO-ready
                structure, schema markup, Core Web Vitals optimization, and clean handoff so your team
                can manage it long after we're done.
              </p>
              <p className="text-gray-500 text-sm">
                Ideal for: content brands, agencies, course creators, service businesses, and DTC
                brands that need a marketing site separate from their store.
              </p>
            </div>

            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <div className="text-3xl mb-3">📦</div>
              <h3 className="font-extrabold text-2xl mb-3">Amazon Brand Stores</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Multi-page Amazon Brand Stores with shoppable modules, video hero banners, category
                navigation, bestseller carousels, and on-brand visual storytelling. We design every
                tile for the click — and structure every page around how Amazon shoppers actually
                browse a brand they're considering.
              </p>
              <Link
                to="/amazon-brand-store-design"
                className="text-cta-green font-bold hover:underline text-sm"
              >
                See brand store builds →
              </Link>
            </div>

            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <div className="text-3xl mb-3">🛠️</div>
              <h3 className="font-extrabold text-2xl mb-3">Custom-Built Websites</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                When Shopify and WordPress don't fit, we build from scratch. React, Next.js, Vite,
                headless commerce, custom dashboards, member areas, lead-gen funnels — bespoke front-
                end work delivered with proper code, version control, and a deployment pipeline you
                actually own.
              </p>
              <p className="text-gray-500 text-sm">
                Ideal for: SaaS, marketplaces, info-product launches, and brands with unique workflows
                that off-the-shelf platforms can't handle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND & CREATIVE SERVICES */}
      <section className="py-20 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-cta-blue font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Brand & Creative Services
          </p>
          <h2 className="font-black text-3xl md:text-5xl text-gray-900 mb-4 text-center leading-tight">
            Design and Words That Carry the Brand.
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-14 leading-relaxed">
            Visuals get the click. Words and design get the sale. Our in-house design and copy team
            owns every word and every pixel that touches a customer — from the first ad they see to
            the unboxing insert in their package.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <div className={sectionCard}>
              <div className="text-3xl mb-3">🎨</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Graphic Design</h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Logos, brand systems, packaging design, social media templates, ad creative, email
                graphics, A+ Content modules, brand store tiles, sales-page hero graphics, lead-magnet
                covers, and pitch decks. Built in Figma and Adobe CC with proper brand guidelines so
                anything we hand off stays on-brand forever.
              </p>
            </div>

            <div className={sectionCard}>
              <div className="text-3xl mb-3">✍️</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Copywriting</h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Amazon listing copy that ranks and converts. Shopify product descriptions that handle
                objections. Sales pages, landing pages, ad copy, email sequences, video scripts, brand
                story, About pages, and packaging copy. Every word written with positioning, voice,
                and conversion psychology in mind.
              </p>
            </div>

            <div className={sectionCard}>
              <div className="text-3xl mb-3">📚</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Long-Form Content</h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                SEO-optimized blog posts (1,500–3,000 words), buyer's guides, lead magnets, downloadable
                PDFs, and editorial features. We research keywords, build topic clusters, write for both
                humans and AI engines, and structure every piece so it actually ranks. Read our{" "}
                <Link to="/blog" className="text-cta-green font-bold hover:underline">
                  blog
                </Link>{" "}
                to see the standard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MARKETING SYSTEMS */}
      <section className="py-20 md:py-24 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <p className="text-cta-green font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Marketing Systems
          </p>
          <h2 className="font-black text-3xl md:text-5xl text-gray-900 mb-4 text-center leading-tight">
            Email, Funnels, and Lead Capture That Compound.
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-14 leading-relaxed">
            Paid traffic gets expensive fast. Email and owned funnels turn one customer into ten
            transactions. We build the systems that make every dollar of ad spend work harder for
            longer.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <div className={sectionCard}>
              <div className="text-3xl mb-3">📧</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Email Marketing</h3>
              <p className="text-gray-700 leading-relaxed text-sm mb-3">
                Klaviyo and Mailchimp build-outs from scratch — or audits and rebuilds of accounts that
                aren't performing. Welcome flows, abandoned cart, browse abandonment, post-purchase,
                winback, VIP segmentation, and weekly campaign calendars. Designed, written, and
                segmented in-house.
              </p>
              <p className="text-gray-500 text-xs">
                Most of our clients see email-attributed revenue grow to 25–40% of total store revenue
                within 90 days.
              </p>
            </div>

            <div className={sectionCard}>
              <div className="text-3xl mb-3">🚀</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Landing Pages & Funnels</h3>
              <p className="text-gray-700 leading-relaxed text-sm mb-3">
                Long-form sales pages, product launch funnels, webinar funnels, lead-magnet funnels,
                and high-converting Shopify pre-purchase landers. Built on Shopify, ClickFunnels,
                Webflow, or custom-coded — whichever makes sense for your stack and your test velocity.
              </p>
              <p className="text-gray-500 text-xs">
                Every funnel is wired to your analytics so we can see exactly where drop-off happens
                and fix it.
              </p>
            </div>

            <div className={sectionCard}>
              <div className="text-3xl mb-3">🎁</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Lead Magnets & Opt-Ins</h3>
              <p className="text-gray-700 leading-relaxed text-sm mb-3">
                Free PDF guides, calculators, quizzes, swipe files, mini-courses, and pop-up offers
                that turn anonymous traffic into a list you own. We design the magnet, write it, design
                it, and wire it into your email platform with proper tagging.
              </p>
              <p className="text-gray-500 text-xs">
                A great lead magnet pays for itself in the first 30 days. A bad one wastes the traffic
                you're already buying.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PAID ADVERTISING + CREATIVE TESTING */}
      <section className="py-20 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-cta-blue font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Paid Advertising + Creative Testing
          </p>
          <h2 className="font-black text-3xl md:text-5xl text-gray-900 mb-4 text-center leading-tight">
            We Make the Creative. Then We Run It as Ads.
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-14 leading-relaxed">
            Most agencies do one or the other — they make creative or they buy media. We do both,
            which is exactly why our ads outperform. The team writing your scripts is the team picking
            the hooks. The team shooting the video is the team reading the CTR data. Faster feedback
            loops. Better ads. Lower CPA.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-14">
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-200">
              <div className="text-3xl mb-3">📘</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Facebook & Instagram Ads</h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Full Meta Ads management — campaign architecture, creative production, daily
                optimization, and weekly reporting. We specialize in DTC ecommerce, info products, and
                lead generation. Advantage+ shopping campaigns, broad targeting with creative-first
                strategy, and proper Conversions API setup so iOS 14+ doesn't blind your data.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-200">
              <div className="text-3xl mb-3">🎬</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">TikTok Ads</h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Native-feeling TikTok creative — produced in-house — paired with Spark Ads, Smart
                Performance Campaigns, and creator partnerships. We treat TikTok as a creative-volume
                game: more concepts, faster iteration, ruthless creative refresh on a 7-day cycle.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-200">
              <div className="text-3xl mb-3">🔍</div>
              <h3 className="font-extrabold text-xl text-gray-900 mb-3">Google Search Ads</h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Google Search, Performance Max, YouTube, and Shopping campaigns. Keyword research,
                bid management, ad copy, landing-page alignment, and conversion tracking. For
                ecommerce brands and lead-gen businesses that need to capture high-intent traffic at
                the bottom of the funnel.
              </p>
            </div>
          </div>

          {/* Split-testing system */}
          <div className="bg-black text-white rounded-3xl p-10 md:p-14">
            <p className="text-cta-green font-extrabold tracking-widest text-xs uppercase mb-3">
              The Evolve Split-Testing System
            </p>
            <h3 className="font-black text-3xl md:text-4xl mb-5 leading-tight">
              The same content runs across every platform. The data tells us which one wins.
            </h3>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Here's the unfair advantage of running creative production and paid media under one
              roof: when we shoot a video, we don't shoot one version. We shoot a hook matrix — 4–8
              opening hooks, 3 body variants, 2–3 CTA variants — and assemble dozens of testable
              edits from one production day.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Then we run those variants across Facebook, TikTok, and Google in parallel. The same
              hook tested against the same audience with the same offer. Within 7 days we know which
              hook, which body, which CTA, and which platform is your real winner. We kill the losers
              and pour budget into the winners.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              Most agencies test 1–2 ads per month. We test 20–40. That's the difference between a
              brand that's "running ads" and a brand that's compounding learnings every week.
            </p>
          </div>
        </div>
      </section>

      {/* THE EVOLVE PROCESS */}
      <section className="py-20 md:py-24 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <p className="text-cta-green font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            The Evolve Process
          </p>
          <h2 className="font-black text-3xl md:text-5xl text-gray-900 mb-14 text-center leading-tight">
            How We Work — Discovery to Optimization.
          </h2>

          <div className="space-y-6">
            {[
              {
                step: "01",
                title: "Discovery",
                body: "A 30–60 minute strategy call where we audit your current content, ads, store, and analytics. We identify the highest-leverage gaps and put a number on what closing them is worth.",
              },
              {
                step: "02",
                title: "Strategy",
                body: "We deliver a written content and creative roadmap: which assets, which platforms, which sequence, which budget. You approve the plan before a single shoot is booked.",
              },
              {
                step: "03",
                title: "Production",
                body: "Our in-house photo, video, music, design, copy, and dev teams execute on the roadmap. Weekly check-ins, shared project workspace, transparent timelines.",
              },
              {
                step: "04",
                title: "Optimization",
                body: "Once assets are live, we measure CTR, CVR, AOV, ROAS, and email engagement. We rebuild the underperformers and double down on the winners. Content is never 'done' — it compounds.",
              },
            ].map((s) => (
              <div
                key={s.step}
                className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 flex flex-col md:flex-row gap-6 items-start"
              >
                <div className="text-cta-green font-black text-5xl leading-none flex-shrink-0">
                  {s.step}
                </div>
                <div>
                  <h3 className="font-extrabold text-2xl text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ONE AGENCY BEATS SIX VENDORS */}
      <section className="py-20 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-cta-blue font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Why It Works
          </p>
          <h2 className="font-black text-3xl md:text-5xl text-gray-900 mb-6 text-center leading-tight">
            One Agency Beats Six Vendors. Every Time.
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-14 leading-relaxed">
            The math is brutal. The brand consistency is invisible. The speed is the multiplier you
            can't fake.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                t: "⚡ Speed",
                b: "When the photo team, video team, copy team, and ads team are in the same Slack, a project that takes 6 weeks across vendors takes 10 days with us. Faster speed-to-market = faster speed-to-revenue.",
              },
              {
                t: "🎨 Brand Consistency",
                b: "Same color palette, same voice, same music, same visual language across every touchpoint. Your customer experiences one brand instead of seven competing ones.",
              },
              {
                t: "💰 Better Unit Economics",
                b: "We bundle services into retainers and project blocks. You get senior strategy, junior execution, and dedicated project management for less than hiring two in-house creatives.",
              },
              {
                t: "📈 Compounding Data",
                b: "Because we own creative + media, we close the feedback loop in days, not months. Every test we run informs the next shoot. Your library of winning content grows every week.",
              },
              {
                t: "🤝 Single Point of Contact",
                b: "One project manager. One creative director. One number to call when something breaks. No finger-pointing between vendors.",
              },
              {
                t: "🛡️ Less Risk",
                b: "We've built content systems for 7 and 8-figure ecommerce brands across Amazon, Shopify, and DTC. We've seen what works in your category — and what's about to stop working.",
              },
            ].map((item) => (
              <div
                key={item.t}
                className="bg-gray-50 rounded-2xl p-7 border border-gray-200"
              >
                <h3 className="font-extrabold text-xl text-gray-900 mb-3">{item.t}</h3>
                <p className="text-gray-700 leading-relaxed">{item.b}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center bg-section-light rounded-2xl p-10">
            <p className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              The real cost of a fragmented content stack?
            </p>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
              We've seen brands save 30–45% on annual content spend after consolidating with us — and{" "}
              <Link to="/ecommerce-content-roi" className="text-cta-green font-bold hover:underline">
                see meaningful conversion lifts
              </Link>{" "}
              within 60 days because the assets finally talk to each other.
            </p>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-20 md:py-24 px-4 bg-black text-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-cta-green font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Brands We've Built For
          </p>
          <h2 className="font-black text-3xl md:text-5xl mb-14 text-center leading-tight">
            7-figure ecommerce brands trust Evolve to own their content.
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <p className="text-cta-green text-3xl font-black mb-2">+47%</p>
              <p className="text-gray-300 leading-relaxed">
                Average lift in Amazon listing conversion rate after Evolve's full content rebuild
                (photo + video + A+ Content + brand store) across 12 client brands measured in 2025.
              </p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <p className="text-cta-green text-3xl font-black mb-2">3.2×</p>
              <p className="text-gray-300 leading-relaxed">
                Average ROAS improvement on Meta + TikTok within 90 days of moving creative production
                in-house with us — driven by faster creative testing cycles and better hook variants.
              </p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <p className="text-cta-green text-3xl font-black mb-2">25–40%</p>
              <p className="text-gray-300 leading-relaxed">
                Of total store revenue attributed to email within 90 days of our Klaviyo flow build-
                outs. The compounding asset every DTC brand should have built two years ago.
              </p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <p className="text-cta-green text-3xl font-black mb-2">63</p>
              <p className="text-gray-300 leading-relaxed">
                Original custom music tracks composed for ecommerce brands and counting. Listen to the
                full library on our{" "}
                <Link to="/custom-jingles-sonic-branding" className="text-cta-green underline">
                  jingles page
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-cta-blue font-extrabold tracking-widest text-xs uppercase mb-3 text-center">
            Frequently Asked Questions
          </p>
          <h2 className="font-black text-3xl md:text-5xl text-gray-900 mb-14 text-center leading-tight">
            Everything you want to ask before booking the call.
          </h2>

          <div className="space-y-5">
            {[
              {
                q: "Do I have to hire you for all of this — or can I just buy one service?",
                a: "Most clients start with one service (usually photo, video, or a Shopify rebuild) and expand into others once they see the work. There's no all-or-nothing minimum. Bundle pricing exists for clients who want more, but single-project work is welcome.",
              },
              {
                q: "How long does a typical project take?",
                a: "Photo shoots: 2–3 weeks from booking to final delivery. Video projects: 3–5 weeks. Shopify builds: 4–8 weeks depending on complexity. Amazon brand stores: 2–4 weeks. WordPress sites: 4–6 weeks. Custom websites: 6–12 weeks. Email build-outs: 2–4 weeks. Paid ads: ongoing monthly retainer with a 30-day onboarding.",
              },
              {
                q: "Can you work with my existing team / agency / freelancers?",
                a: "Yes. We regularly slot into existing teams as the creative production arm, or work alongside in-house marketing teams who handle strategy and want us to execute. We can hand off raw files, source code, design files — whatever your team needs to keep moving after we're done.",
              },
              {
                q: "Do you take equity, retainers, or one-off project fees?",
                a: "All three are options. Most relationships start as one-off projects, expand into monthly retainers, and (for the right brands) we'll explore performance-based or equity arrangements. Pricing is discussed on the discovery call once we understand scope.",
              },
              {
                q: "How is this different from hiring an in-house team?",
                a: "An in-house creative team of one photographer, one videographer, one designer, one copywriter, one developer, one ads buyer, and one project manager runs roughly $600K–$900K/year fully loaded. We deliver the same output for a fraction of that — and you don't carry the management overhead, the recruiting, or the firing risk.",
              },
              {
                q: "Do you specialize in any particular vertical?",
                a: "Our deepest expertise is Amazon FBA brands, DTC consumer products, and Shopify ecommerce — particularly in supplements, beauty, home goods, kitchen, fitness, and pet. We've also built content systems for SaaS, info-products, and service businesses.",
              },
              {
                q: "What happens after I book the strategy call?",
                a: "We send a short pre-call questionnaire. On the call we audit your current content, store, and ads (live, screen-shared). Within 48 hours after the call you'll get a written recommendation with scoped projects and pricing. No pressure, no proposal-by-PowerPoint, no follow-up sales pestering.",
              },
              {
                q: "Are you AI-friendly? I want my brand to be recommended by ChatGPT.",
                a: (
                  <>
                    Yes — we build content with both traditional SEO and Generative Engine Optimization
                    (GEO) in mind. Read our 2026 GEO playbook on our{" "}
                    <Link to="/blog" className="text-cta-green font-bold hover:underline">
                      blog
                    </Link>{" "}
                    for the full framework. Every page, every guide, every product description we
                    write is structured to be cited by ChatGPT, Perplexity, Gemini, and Google AI
                    Overviews.
                  </>
                ),
              },
            ].map((f, i) => (
              <details
                key={i}
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 group"
              >
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex justify-between items-start gap-4">
                  <span>{f.q}</span>
                  <span className="text-cta-green text-2xl flex-shrink-0 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-gray-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 px-4 bg-cta-green text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-black text-4xl md:text-6xl mb-6 leading-tight">
            Ready to consolidate your content into one team that actually owns it?
          </h2>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed">
            Book a free 30-minute strategy call. We'll audit your current content, store, and ads
            live — and tell you exactly what to fix first.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/evolvemedia/amz-30-min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white font-bold px-10 py-5 rounded-full hover:opacity-90 transition text-lg"
            >
              Book a Free Strategy Call
            </a>
            <a
              href="https://emapdf.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-cta-green font-bold px-10 py-5 rounded-full hover:opacity-90 transition text-lg"
            >
              Get the Free Ecom Profit Box
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
